// src/components/auth/ProtectedRoute.tsx
import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import LoadingSpinner from '../ui/LoadingSpinner';
import type { User } from '../../types';
import {
  isAccountDisabled,
  isSystemManager,
} from '../../utils/authorization';
import { hasEmployeePermission } from '../../utils/employeePermissions';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRole?: 'teacher' | 'student' | 'parent' | 'admin' | 'employee';
  allowedRoles?: User['role'][];
  requiredPermission?: keyof NonNullable<User['permissions']>;
  redirectTo?: string;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requiredRole,
  allowedRoles,
  requiredPermission,
  redirectTo = '/login',
}) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className='min-h-screen flex items-center justify-center'>
        <LoadingSpinner size='large' />
      </div>
    );
  }

  if (!user) {
    return <Navigate to={redirectTo} replace />;
  }

  if (isAccountDisabled(user)) {
    return <Navigate to='/login' replace />;
  }

  const roleDenied =
    (requiredRole &&
      (requiredRole === 'admin'
        ? !isSystemManager(user)
        : user.role !== requiredRole)) ||
    (allowedRoles && !allowedRoles.includes(user.role));
  const permissionDenied =
    requiredPermission && !hasEmployeePermission(user, requiredPermission);

  if (roleDenied || permissionDenied) {
    return <Navigate to='/dashboard' replace />;
  }

  return <>{children}</>;
};

export default ProtectedRoute;
